import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  MdAdd,
  MdArrowDownward,
  MdArrowForward,
  MdArrowUpward,
  MdClose,
  MdEdit,
  MdExpandMore,
  MdInfoOutline,
  MdMarkEmailRead,
  MdPersonAdd,
  MdRoute,
  MdSend,
  MdSync,
  MdUnfoldMore,
  MdUploadFile,
} from "react-icons/md";
import { getApiErrorMessage } from "api";
import AlertPane from "components/alert";
import Badge from "components/badge";
import Button, { IconButton } from "components/button";
import Card, { CardHeader } from "components/card";
import { Pagination } from "components/data-table";
import { Checkbox, InputField, SearchInput, Select, Textarea } from "components/fields";
import { PageContainer, PageHeader } from "components/page";
import Skeleton from "components/skeleton/Skeleton";
import StatCard from "components/stat-card";
import { EmptyState, ErrorState } from "components/states";
import { SegmentedControl } from "components/tabs";
import { Tooltip } from "components/tooltip";
import { useAuth } from "hooks/useAuth";
import notify from "lib/notify";
import pathwaysService from "services/pathwaysService";
import { EMPTY, formatDate } from "utils/format";
import AddGuestModal from "./AddGuestModal";
import CsvImportModal from "./CsvImportModal";

const SURVEY_STATUSES = {
  not_sent: { label: "Not Sent", tone: "neutral" },
  minor: { label: "Child", tone: "warning" },
  queued: { label: "Scheduled", tone: "info" },
  sent: { label: "Sent", tone: "info" },
  opened: { label: "Opened", tone: "brand" },
  completed: { label: "Completed", tone: "success" },
};

// Guest Insights bars: the most common answer is emphasized.
const INSIGHT_BAR_CLASSES = {
  top: "bg-primary dark:bg-accent",
  other: "bg-accent/70 dark:bg-brand-700",
};

const SEND_MODE_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "auto", label: "Automatic" },
];

const DELIVERY_OPTIONS = [
  { value: "email", label: "Email" },
  { value: "sms", label: "SMS" },
  { value: "auto", label: "Auto" },
];

const TH_CLASS = "px-4 py-3 first:pl-6 last:pr-6 text-left text-xs font-medium uppercase tracking-wide text-fg-muted";

const statusBadge = (status) => {
  const s = SURVEY_STATUSES[status] || SURVEY_STATUSES.not_sent;
  return <Badge tone={s.tone}>{s.label}</Badge>;
};

const UsageCard = ({ used, limit, orgName }) => {
  const pct = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  let barClass = "bg-primary dark:bg-accent";
  let message = null;
  if (pct >= 100) {
    barClass = "bg-danger";
    message = (
      <p className="mt-1 text-xs font-medium text-danger-fg">
        Monthly limit reached. New surveys are paused.
      </p>
    );
  } else if (pct >= 80) {
    barClass = "bg-warning";
    message = <p className="mt-1 text-xs font-medium text-warning-fg">Approaching monthly limit</p>;
  }

  return (
    <Card padding="md">
      <div className="flex items-center gap-1">
        <p className="text-sm font-medium text-fg-muted">Monthly Usage</p>
        <Tooltip
          content={`Your free tier includes ${limit} guest surveys per month. Upgrade for unlimited access.`}
          trigger={
            <button
              type="button"
              aria-label="About Monthly Usage"
              className="focus-ring rounded-full text-fg-muted transition-colors hover:text-fg"
            >
              <MdInfoOutline aria-hidden="true" className="h-4 w-4" />
            </button>
          }
        />
      </div>
      <div className="mt-2 flex justify-between text-sm">
        <span className="text-fg-muted">
          {used} / {limit}
        </span>
        <span className="font-medium text-fg">{Math.round(pct)}%</span>
      </div>
      <div
        className="mt-1 h-2.5 w-full overflow-hidden rounded-full bg-surface-subtle"
        role="progressbar"
        aria-label="Monthly survey usage"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(pct)}
      >
        <div className={`h-full rounded-full transition-all ${barClass}`} style={{ width: `${pct}%` }} />
      </div>
      {message}
      <a
        href={`mailto:sales@atriumoverview.com?subject=${encodeURIComponent(`${orgName}: Account Upgrade`)}`}
        className="focus-ring mt-2 inline-flex items-center gap-1 self-start rounded text-xs font-semibold text-primary hover:underline dark:text-accent"
      >
        Need more? Contact Sales
        <MdArrowForward aria-hidden="true" className="h-3.5 w-3.5" />
      </a>
    </Card>
  );
};

const Accordion = ({ id, title, children, onEditClick, editing }) => {
  const [open, setOpen] = useState(false);
  const panelId = `${id}-panel`;
  return (
    <Card variant="outlined" className="overflow-hidden">
      <div className="flex items-center gap-2 p-1 pr-3">
        <button
          type="button"
          onClick={() => setOpen(!open)}
          aria-expanded={open}
          aria-controls={panelId}
          className="focus-ring flex flex-1 items-center justify-between rounded-control px-5 py-4 text-left transition-colors hover:bg-surface-subtle"
        >
          <span className="font-semibold text-fg">{title}</span>
          <MdExpandMore
            aria-hidden="true"
            className={`h-5 w-5 text-fg-muted transition-transform ${open ? "rotate-180" : ""}`}
          />
        </button>
        {onEditClick && !editing && (
          <IconButton
            icon={<MdEdit />}
            label={`Edit ${title} Questions`}
            size="sm"
            onClick={() => {
              if (!open) setOpen(true);
              onEditClick();
            }}
          />
        )}
      </div>
      {open && (
        <div id={panelId} className="space-y-4 border-t border-line px-5 py-4">
          {children}
        </div>
      )}
    </Card>
  );
};

const optionText = (c) => (typeof c === "string" ? c : c.text || c.label || JSON.stringify(c));

const QuestionList = ({ questions }) => {
  if (!questions || questions.length === 0) {
    return <EmptyState variant="inline" title="No questions found" />;
  }
  return (
    <ol className="list-inside list-decimal space-y-3">
      {questions.map((q, i) => {
        const opts = q.options || q.choices || q.answerChoices || [];
        return (
          <li key={i} className="text-sm text-fg">
            <span className="font-medium">{q.question || q.text}</span>
            {opts.length > 0 && (
              <ul className="ml-6 mt-1 list-disc space-y-0.5">
                {opts.map((c, j) => (
                  <li key={j} className="text-xs text-fg-muted">
                    {optionText(c)}
                  </li>
                ))}
              </ul>
            )}
          </li>
        );
      })}
    </ol>
  );
};

const EditableQuestionList = ({ questions, onChange, onSave, onCancel, saving }) => {
  if (!questions || questions.length === 0) {
    return <EmptyState variant="inline" title="No questions found" />;
  }

  const updateQuestion = (idx, field, value) => {
    const updated = questions.map((q, i) => (i === idx ? { ...q, [field]: value } : q));
    onChange(updated);
  };

  const updateOption = (qIdx, optIdx, value) => {
    const updated = questions.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...(q.options || q.choices || q.answerChoices || [])];
      opts[optIdx] = value;
      return { ...q, options: opts };
    });
    onChange(updated);
  };

  const addOption = (qIdx) => {
    const updated = questions.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = [...(q.options || q.choices || q.answerChoices || []), ""];
      return { ...q, options: opts };
    });
    onChange(updated);
  };

  const removeOption = (qIdx, optIdx) => {
    const updated = questions.map((q, i) => {
      if (i !== qIdx) return q;
      const opts = (q.options || q.choices || q.answerChoices || []).filter((_, j) => j !== optIdx);
      return { ...q, options: opts };
    });
    onChange(updated);
  };

  return (
    <div className="space-y-5">
      <ol className="space-y-5">
        {questions.map((q, i) => {
          const opts = q.options || q.choices || q.answerChoices || [];
          return (
            <li key={q.id || i} className="space-y-2">
              <Textarea
                label={`Question ${i + 1}`}
                value={q.text || q.question || ""}
                onChange={(e) => updateQuestion(i, "text", e.target.value)}
                rows={2}
              />
              {opts.length > 0 && (
                <div className="ml-4 space-y-2">
                  {opts.map((opt, j) => (
                    <div key={j} className="flex items-center gap-2">
                      <InputField
                        aria-label={`Question ${i + 1} option ${j + 1}`}
                        className="flex-1"
                        value={typeof opt === "string" ? opt : opt.text || opt.label || ""}
                        onChange={(e) => updateOption(i, j, e.target.value)}
                      />
                      <IconButton
                        icon={<MdClose />}
                        label={`Remove Option ${j + 1}`}
                        size="sm"
                        onClick={() => removeOption(i, j)}
                      />
                    </div>
                  ))}
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<MdAdd aria-hidden="true" />}
                    onClick={() => addOption(i)}
                  >
                    Add Option
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ol>
      <div className="flex items-center justify-end gap-2 border-t border-line pt-4">
        <Button variant="secondary" onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button onClick={onSave} loading={saving}>
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </div>
    </div>
  );
};

const SortableHeader = ({ label, col, sortBy, sortOrder, onSort, title }) => {
  const active = sortBy === col;
  const Icon = active ? (sortOrder === "ASC" ? MdArrowUpward : MdArrowDownward) : MdUnfoldMore;
  return (
    <th
      className={TH_CLASS}
      aria-sort={active ? (sortOrder === "ASC" ? "ascending" : "descending") : undefined}
    >
      <button
        type="button"
        onClick={() => onSort(col)}
        title={title}
        className="focus-ring inline-flex items-center gap-1 rounded uppercase tracking-wide transition-colors hover:text-fg"
      >
        {label}
        <Icon aria-hidden="true" className="h-3.5 w-3.5" />
      </button>
    </th>
  );
};

const GuestResponses = ({ loading, responses }) => {
  if (loading) return <Skeleton lines={3} className="max-w-md" />;
  if (!responses || responses.length === 0) {
    return <EmptyState variant="inline" title="No responses yet" />;
  }
  return (
    <div className="space-y-4">
      {responses.map((r, idx) => {
        const questions = r.campaign?.questions || [];
        const answersObj = typeof r.answers === "string" ? JSON.parse(r.answers) : r.answers || {};
        const questionEntries = Object.entries(answersObj);

        if (questionEntries.length === 0) {
          return (
            <p key={idx} className="text-sm text-fg-muted">
              No answers recorded.
            </p>
          );
        }

        return (
          <div key={idx}>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-fg-muted">
              {r.campaign?.name || "Survey Response"}
            </p>
            <ul className="space-y-2">
              {questionEntries.map(([qId, answer]) => {
                const q = questions.find((item) => item.id === qId);
                return (
                  <li key={qId} className="text-sm">
                    <span className="font-medium text-fg">{q?.text || qId}:</span>{" "}
                    <span className="text-fg-muted">
                      {Array.isArray(answer) ? answer.join(", ") : answer}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
};

const DashboardSkeleton = () => (
  <>
    <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-32 rounded-card" />
      ))}
    </div>
    <Skeleton className="h-64 rounded-card" />
    <Skeleton className="h-96 rounded-card" />
  </>
);

const PathwaysDashboard = () => {
  const { user } = useAuth();
  const orgName = user?.organizationName || "Our Church";

  // Dashboard stats
  const [dashboard, setDashboard] = useState(null);
  const [dashLoading, setDashLoading] = useState(true);
  const [dashError, setDashError] = useState(null);

  // PCO sync
  const [syncing, setSyncing] = useState(false);

  // Survey questions
  const [surveyQuestions, setSurveyQuestions] = useState(null);
  const [sqLoading, setSqLoading] = useState(true);
  const [sqError, setSqError] = useState(null);

  // Survey editing
  const [editingInitial, setEditingInitial] = useState(false);
  const [editingFollowup, setEditingFollowup] = useState(false);
  const [editedInitial, setEditedInitial] = useState(null);
  const [editedFollowup, setEditedFollowup] = useState(null);
  const [savingSurvey, setSavingSurvey] = useState(false);

  // Guests table
  const [guests, setGuests] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, per: 20, totalCount: 0, totalPages: 1 });
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [membershipFilter, setMembershipFilter] = useState("");
  const [sortBy, setSortBy] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [guestsLoading, setGuestsLoading] = useState(true);
  const [guestsError, setGuestsError] = useState(null);

  // Expanded row
  const [expandedGuestId, setExpandedGuestId] = useState(null);
  const [responses, setResponses] = useState({});
  const [responsesLoading, setResponsesLoading] = useState({});

  // Guest management modals
  const [showAddGuest, setShowAddGuest] = useState(false);
  const [showImport, setShowImport] = useState(false);

  const [savingSettings, setSavingSettings] = useState(false);
  const [sendingForGuestId, setSendingForGuestId] = useState(null);

  const refreshDashboard = () =>
    pathwaysService
      .getDashboard()
      .then((r) => setDashboard(r.data?.data || r.data))
      .catch(() => {});

  const loadDashboard = useCallback(() => {
    setDashLoading(true);
    setDashError(null);
    pathwaysService
      .getDashboard()
      .then((res) => setDashboard(res.data?.data || res.data))
      .catch((err) => setDashError(err || new Error("Failed to load dashboard")))
      .finally(() => setDashLoading(false));
  }, []);

  const loadSurveyQuestions = useCallback(() => {
    setSqLoading(true);
    setSqError(null);
    pathwaysService
      .getSurveyQuestions()
      .then((res) => setSurveyQuestions(res.data?.data || res.data))
      .catch((err) => setSqError(err))
      .finally(() => setSqLoading(false));
  }, []);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    loadSurveyQuestions();
  }, [loadSurveyQuestions]);

  // Fetch guests
  const fetchGuests = useCallback(
    (page = 1, searchTerm = search, status = statusFilter, sb = sortBy, so = sortOrder, mship = membershipFilter) => {
      setGuestsLoading(true);
      setGuestsError(null);
      pathwaysService
        .getGuests({
          page,
          per: pagination.per,
          search: searchTerm || undefined,
          surveyStatus: status || undefined,
          membership: mship || undefined,
          sortBy: sb,
          sortOrder: so,
        })
        .then((res) => {
          const d = res.data?.data || res.data;
          setGuests(d.guests || []);
          setPagination((prev) => ({ ...prev, ...(d.pagination || {}), page }));
        })
        .catch((err) => setGuestsError(err))
        .finally(() => setGuestsLoading(false));
    },
    [search, statusFilter, membershipFilter, sortBy, sortOrder, pagination.per]
  );

  useEffect(() => {
    fetchGuests(1, "");
  }, []);

  // SearchInput debounces, so each call is a settled term.
  const handleSearchChange = (val) => {
    setSearch(val);
    fetchGuests(1, val, statusFilter, sortBy, sortOrder);
  };

  const handleStatusFilter = (val) => {
    setStatusFilter(val);
    fetchGuests(1, search, val, sortBy, sortOrder, membershipFilter);
  };

  const handleMembershipFilter = (val) => {
    setMembershipFilter(val);
    fetchGuests(1, search, statusFilter, sortBy, sortOrder, val);
  };

  const clearGuestFilters = () => {
    setSearch("");
    setStatusFilter("");
    setMembershipFilter("");
    fetchGuests(1, "", "", sortBy, sortOrder, "");
  };

  const handleSort = (column) => {
    const newOrder = sortBy === column && sortOrder === "ASC" ? "DESC" : "ASC";
    setSortBy(column);
    setSortOrder(newOrder);
    fetchGuests(1, search, statusFilter, column, newOrder);
  };

  const updateSettings = async (patch) => {
    setSavingSettings(true);
    // Optimistic update
    setDashboard((prev) => ({ ...prev, settings: { ...(prev?.settings || {}), ...patch } }));
    try {
      await pathwaysService.updateSettings(patch);
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not save survey settings"));
      // Revert on failure
      const fresh = await pathwaysService.getDashboard();
      setDashboard(fresh.data?.data || fresh.data);
    } finally {
      setSavingSettings(false);
    }
  };

  const toggleMembershipEligibility = (membership) => {
    const current = settings.membershipFilter;
    let next;
    if (current === null || current === undefined) {
      // "All" → switch to a specific list of everything-except this one
      next = (dashboard?.membershipTypes || []).filter((m) => m !== membership);
      // If user is also using "(No membership)", keep that option enabled by default
      if (membership !== "__none__" && (dashboard?.membershipTypes || []).length > 0) next.push("__none__");
    } else if (current.includes(membership)) {
      next = current.filter((m) => m !== membership);
    } else {
      next = [...current, membership];
    }
    updateSettings({ membershipFilter: next });
  };

  const setMembershipFilterAll = () => updateSettings({ membershipFilter: null });

  const handleSendNow = async (guestId) => {
    setSendingForGuestId(guestId);
    try {
      await pathwaysService.sendSurvey(guestId);
      notify.success("Survey sent");
      fetchGuests(pagination.page, search, statusFilter, sortBy, sortOrder);
      refreshDashboard();
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not send survey"));
    } finally {
      setSendingForGuestId(null);
    }
  };

  // Manual PCO sync
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await pathwaysService.syncPco();
      const count = res.data?.data?.syncedCount ?? 0;
      notify.success(`Sync complete: ${count} total guests`);
      refreshDashboard();
      fetchGuests(1, search);
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Sync failed. Try again."));
    } finally {
      setSyncing(false);
    }
  };

  // Survey editing handlers
  const startEditInitial = () => {
    setEditedInitial(JSON.parse(JSON.stringify(surveyQuestions?.initial || [])));
    setEditingInitial(true);
  };
  const startEditFollowup = () => {
    setEditedFollowup(JSON.parse(JSON.stringify(surveyQuestions?.followup || [])));
    setEditingFollowup(true);
  };
  const cancelEditInitial = () => { setEditingInitial(false); setEditedInitial(null); };
  const cancelEditFollowup = () => { setEditingFollowup(false); setEditedFollowup(null); };

  const saveInitialQuestions = async () => {
    setSavingSurvey(true);
    try {
      await pathwaysService.approveSurvey(editedInitial);
      setSurveyQuestions((prev) => ({ ...prev, initial: editedInitial }));
      setEditingInitial(false);
      setEditedInitial(null);
      notify.success("Survey questions saved");
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not save survey questions"));
    } finally {
      setSavingSurvey(false);
    }
  };

  const saveFollowupQuestions = async () => {
    setSavingSurvey(true);
    try {
      await pathwaysService.approveSurvey(editedFollowup);
      setSurveyQuestions((prev) => ({ ...prev, followup: editedFollowup }));
      setEditingFollowup(false);
      setEditedFollowup(null);
      notify.success("Survey questions saved");
    } catch (err) {
      notify.error(getApiErrorMessage(err, "Could not save survey questions"));
    } finally {
      setSavingSurvey(false);
    }
  };

  // Row expansion
  const toggleRow = (guestId) => {
    if (expandedGuestId === guestId) {
      setExpandedGuestId(null);
      return;
    }
    setExpandedGuestId(guestId);
    if (!responses[guestId]) {
      setResponsesLoading((prev) => ({ ...prev, [guestId]: true }));
      pathwaysService
        .getGuestResponses(guestId)
        .then((res) => {
          const d = res.data?.data || res.data;
          setResponses((prev) => ({ ...prev, [guestId]: d }));
        })
        .catch(() => {
          setResponses((prev) => ({ ...prev, [guestId]: [] }));
        })
        .finally(() => {
          setResponsesLoading((prev) => ({ ...prev, [guestId]: false }));
        });
    }
  };

  const header = <PageHeader title="Pathways" />;

  if (dashLoading) {
    return (
      <PageContainer>
        {header}
        <DashboardSkeleton />
      </PageContainer>
    );
  }

  // Show setup prompt if onboarding hasn't been completed
  if (dashError && (!user?.pathwaysOnboardingCompleted || (dashError.message || "").includes("onboarding"))) {
    return (
      <PageContainer>
        {header}
        <Card padding="md">
          <EmptyState
            icon={MdRoute}
            title="Set Up Pathways"
            description="Pathways helps you engage first-time guests with automated surveys and personalized follow-ups. Connect your Planning Center account to get started."
            action={
              <Button
                as={Link}
                to="/admin/pathways/onboarding"
                rightIcon={<MdArrowForward aria-hidden="true" />}
              >
                Get Started
              </Button>
            }
          />
        </Card>
      </PageContainer>
    );
  }

  if (dashError) {
    return (
      <PageContainer>
        {header}
        <Card padding="md">
          <ErrorState
            message={getApiErrorMessage(dashError, "Could not load the Pathways dashboard.")}
            onRetry={loadDashboard}
          />
        </Card>
      </PageContainer>
    );
  }

  const {
    ftgCount = 0,
    newGuestsCount = 0,
    surveysSent = 0,
    monthlyLimit = 0,
    unreachableCount = 0,
    emailAnalytics = {},
    settings = {},
    membershipTypes = [],
  } = dashboard || {};

  const { completed = 0, openRate = 0 } = emailAnalytics;
  const deliveryMethod = settings.deliveryMethod || "sms";
  const sendMode = settings.sendMode || "manual";
  const membershipFilterSetting = settings.membershipFilter; // null = all, [] = none, [x,y] = specific
  const isMembershipFilterAll = membershipFilterSetting === null || membershipFilterSetting === undefined;
  const selectedMemberships = Array.isArray(membershipFilterSetting) ? membershipFilterSetting : [];

  const hasSurveyCampaigns = surveyQuestions?.initial?.length > 0;
  const hasGuestFilters = Boolean(search || statusFilter || membershipFilter);
  const membershipChoices = [
    ...membershipTypes.map((m) => ({ value: m, label: m })),
    { value: "__none__", label: "Blank" },
  ];

  const renderGuestTable = () => {
    if (guestsError) {
      return (
        <ErrorState
          message={getApiErrorMessage(guestsError, "Could not load guests.")}
          onRetry={() => fetchGuests(pagination.page)}
        />
      );
    }

    if (!guestsLoading && guests.length === 0) {
      return hasGuestFilters ? (
        <EmptyState
          variant="filtered"
          title="No guests match your filters"
          action={
            <Button variant="ghost" onClick={clearGuestFilters}>
              Clear Filters
            </Button>
          }
        />
      ) : (
        <EmptyState
          icon={MdPersonAdd}
          title="No Guests Yet"
          description="Guests synced from Planning Center, imported from a CSV or added by hand appear here."
        />
      );
    }

    return (
      <div className="-mx-6 -mb-6 border-t border-line">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" aria-busy={guestsLoading || undefined}>
            <thead className="border-b border-line">
              <tr>
                <SortableHeader label="First Name" col="firstName" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader label="Last Name" col="lastName" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
                <SortableHeader
                  label="Email"
                  col="email"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  title="Sort by has email / missing email"
                />
                <SortableHeader
                  label="Phone"
                  col="phone"
                  sortBy={sortBy}
                  sortOrder={sortOrder}
                  onSort={handleSort}
                  title="Sort by has phone / missing phone"
                />
                <th className={TH_CLASS}>Membership</th>
                <th className={TH_CLASS}>Survey Status</th>
                <SortableHeader label="Date Added" col="createdAt" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />
              </tr>
            </thead>
            <tbody>
              {guestsLoading
                ? Array.from({ length: 5 }, (_, i) => (
                    <tr key={i} className="border-b border-line last:border-b-0 [&>td:first-child]:pl-6 [&>td:last-child]:pr-6">
                      {Array.from({ length: 7 }, (__, j) => (
                        <td key={j} className="px-4 py-3">
                          <Skeleton className="h-4 w-full" />
                        </td>
                      ))}
                    </tr>
                  ))
                : guests.map((g) => {
                    const expanded = expandedGuestId === g.id;
                    const canSend =
                      sendMode === "manual" &&
                      g.surveyStatus === "not_sent" &&
                      ((g.email && !g.email.includes("@placeholder.local")) || (g.phone && g.phone !== ""));
                    return (
                      <React.Fragment key={g.id}>
                        <tr
                          onClick={(e) => {
                            if (e.target.closest("button, a")) return;
                            toggleRow(g.id);
                          }}
                          onKeyDown={(e) => {
                            if (e.target !== e.currentTarget) return;
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              toggleRow(g.id);
                            }
                          }}
                          tabIndex={0}
                          aria-expanded={expanded}
                          className="cursor-pointer border-b border-line transition-colors last:border-b-0 [&>td:first-child]:pl-6 [&>td:last-child]:pr-6 hover:bg-surface-subtle focus-visible:bg-surface-subtle focus-visible:outline-none"
                        >
                          <td className="px-4 py-3 text-fg">{g.firstName}</td>
                          <td className="px-4 py-3 text-fg">{g.lastName}</td>
                          <td className="px-4 py-3 text-fg-muted">
                            {g.email?.includes("@placeholder.local") ? (
                              <span className="text-xs italic text-warning-fg">No email on file</span>
                            ) : (
                              g.email
                            )}
                          </td>
                          <td className="px-4 py-3 text-fg-muted">{g.phone || EMPTY}</td>
                          <td className="px-4 py-3 text-fg-muted">{g.membership || EMPTY}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {statusBadge(g.surveyStatus)}
                              {canSend && (
                                <Button
                                  size="sm"
                                  leftIcon={<MdSend aria-hidden="true" />}
                                  loading={sendingForGuestId === g.id}
                                  onClick={() => handleSendNow(g.id)}
                                >
                                  {sendingForGuestId === g.id ? "Sending…" : "Send Survey"}
                                </Button>
                              )}
                            </div>
                          </td>
                          <td className="whitespace-nowrap px-4 py-3 text-fg-muted">{formatDate(g.createdAt)}</td>
                        </tr>
                        {expanded && (
                          <tr className="border-b border-line last:border-b-0">
                            <td colSpan={7} className="bg-surface-raised px-6 py-4">
                              <GuestResponses loading={responsesLoading[g.id]} responses={responses[g.id]} />
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
            </tbody>
          </table>
        </div>
        <Pagination
          className="border-t border-line px-6 py-3"
          pageIndex={pagination.page - 1}
          pageSize={pagination.per}
          total={pagination.totalCount}
          onPageChange={(index) => fetchGuests(index + 1)}
        />
      </div>
    );
  };

  return (
    <PageContainer>
      {header}

      {!sqLoading && !sqError && !hasSurveyCampaigns && (
        <AlertPane tone="warning" title="Survey Questions Not Set Up" className="">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p>
              Your guest survey questions need to be reviewed and approved before surveys can be sent automatically.
              This only takes a minute.
            </p>
            <Button
              as={Link}
              to="/admin/pathways/onboarding?step=survey"
              size="sm"
              rightIcon={<MdArrowForward aria-hidden="true" />}
            >
              Set Up Survey
            </Button>
          </div>
        </AlertPane>
      )}

      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
        <StatCard
          icon={MdPersonAdd}
          accent="sage"
          label="New Guests"
          value={newGuestsCount}
          caption="Rolling 30 days"
          info="First-time guests with a valid email or phone number: the ones who can actually receive a survey."
        />
        <StatCard
          icon={MdSend}
          accent="sky"
          label="Surveys Sent"
          value={surveysSent}
          caption="This month"
          info="Total survey invitations sent to guests this month via email or SMS."
        />
        <StatCard
          icon={MdMarkEmailRead}
          accent="gold"
          label="Email Analytics"
          value={`${Math.round(openRate)}%`}
          caption={`Open rate · ${completed} completed`}
          info="Percentage of sent surveys that were opened or completed by guests."
        />
        <UsageCard used={ftgCount} limit={monthlyLimit} orgName={orgName} />
      </div>

      {unreachableCount > 0 && settings?.setupMode !== "manual" && (
        <AlertPane tone="info" className="">
          <span className="font-semibold">
            {unreachableCount} guest{unreachableCount !== 1 ? "s" : ""}
          </span>{" "}
          synced from Planning Center {unreachableCount !== 1 ? "have" : "has"} no email or phone on file and
          won't receive a survey. They'll be counted once contact info is added in Planning Center.
        </AlertPane>
      )}

      {dashboard?.insights && dashboard.insights.length > 0 && dashboard.insights.some((q) => q.totalResponses > 0) && (
        <Card padding="md">
          <CardHeader
            title="Guest Insights"
            actions={
              <span className="text-xs text-fg-muted">
                {dashboard.insights[0]?.totalResponses || 0} responses
              </span>
            }
          />
          <div className="mt-5 grid grid-cols-1 gap-x-10 gap-y-6 lg:grid-cols-2">
            {dashboard.insights.map((q) => (
              <div key={q.questionId}>
                <p className="mb-2 text-sm font-semibold text-fg">{q.questionText}</p>
                <div className="space-y-1.5">
                  {[...q.distribution]
                    .sort((a, b) => b.count - a.count)
                    .map((d, i) => (
                      <div key={i} className="flex h-7 items-center gap-2.5">
                        <span className="w-[42%] shrink-0 truncate text-xs text-fg" title={d.option}>
                          {d.option}
                        </span>
                        <div className="h-3 flex-1 overflow-hidden rounded-full bg-surface-subtle">
                          {d.count > 0 && (
                            <div
                              className={`h-full rounded-full transition-all ${
                                i === 0 ? INSIGHT_BAR_CLASSES.top : INSIGHT_BAR_CLASSES.other
                              }`}
                              style={{ width: `${Math.max(d.percentage, 3)}%` }}
                            />
                          )}
                        </div>
                        <span className="w-9 shrink-0 text-right text-xs font-medium tabular-nums text-fg-muted">
                          {d.percentage}%
                        </span>
                      </div>
                    ))}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      <section className="flex flex-col gap-3" aria-labelledby="survey-questions-heading">
        <h2 id="survey-questions-heading" className="text-heading">
          Survey Questions
        </h2>
        {sqLoading ? (
          <>
            <Skeleton className="h-14 rounded-card" />
            <Skeleton className="h-14 rounded-card" />
          </>
        ) : sqError ? (
          <Card padding="md">
            <ErrorState
              message={getApiErrorMessage(sqError, "Could not load survey questions.")}
              onRetry={loadSurveyQuestions}
            />
          </Card>
        ) : (
          <>
            <Accordion
              id="initial-survey"
              title="Initial FTG Survey"
              onEditClick={startEditInitial}
              editing={editingInitial}
            >
              {editingInitial ? (
                <EditableQuestionList
                  questions={editedInitial}
                  onChange={setEditedInitial}
                  onSave={saveInitialQuestions}
                  onCancel={cancelEditInitial}
                  saving={savingSurvey}
                />
              ) : (
                <QuestionList questions={surveyQuestions?.initial || []} />
              )}
            </Accordion>
            <Accordion
              id="followup-survey"
              title="4-Week Follow-up"
              onEditClick={startEditFollowup}
              editing={editingFollowup}
            >
              {editingFollowup ? (
                <EditableQuestionList
                  questions={editedFollowup}
                  onChange={setEditedFollowup}
                  onSave={saveFollowupQuestions}
                  onCancel={cancelEditFollowup}
                  saving={savingSurvey}
                />
              ) : (
                <QuestionList questions={surveyQuestions?.followup || []} />
              )}
            </Accordion>
          </>
        )}
      </section>

      <Card padding="md">
        <CardHeader title="Survey Delivery" />
        <div className="mt-4 flex flex-col gap-5 lg:flex-row lg:gap-8">
          <div className="flex-1">
            <p className="text-sm font-semibold text-fg">Send Mode</p>
            <p className="mb-3 mt-0.5 text-xs text-fg-muted">
              {sendMode === "auto"
                ? "New first-time guests are automatically queued and sent a survey within the daily window."
                : "You'll manually choose when to send each survey from the table below."}
            </p>
            <SegmentedControl
              aria-label="Send mode"
              options={SEND_MODE_OPTIONS}
              value={sendMode}
              onChange={(val) => updateSettings({ sendMode: val })}
              disabled={savingSettings}
            />
          </div>
          <div className="flex-1">
            <p className="text-sm font-semibold text-fg">Delivery Method</p>
            <p className="mb-3 mt-0.5 text-xs text-fg-muted">
              {deliveryMethod === "email" && "Surveys go out via email only. Guests without email are skipped."}
              {deliveryMethod === "sms" && "Surveys go out via SMS only. Guests without phone are skipped."}
              {deliveryMethod === "auto" && "Email when available, SMS as a fallback when email isn't on file."}
            </p>
            <SegmentedControl
              aria-label="Delivery method"
              options={DELIVERY_OPTIONS}
              value={deliveryMethod}
              onChange={(val) => updateSettings({ deliveryMethod: val })}
              disabled={savingSettings}
            />
          </div>
        </div>

        {membershipTypes.length > 0 && (
          <div className="mt-6 border-t border-line pt-5" role="group" aria-labelledby="eligible-memberships-heading">
            <div className="mb-2 flex items-center justify-between">
              <p id="eligible-memberships-heading" className="text-sm font-semibold text-fg">
                Eligible Membership Types
              </p>
              <Button
                variant="ghost"
                size="sm"
                onClick={setMembershipFilterAll}
                disabled={savingSettings || isMembershipFilterAll}
              >
                Send to All
              </Button>
            </div>
            <p className="mb-3 text-xs text-fg-muted">
              {isMembershipFilterAll
                ? settings?.setupMode === "manual"
                  ? "Surveys are queued for every guest, regardless of membership type."
                  : "Surveys are queued for every guest synced from PCO, regardless of membership type."
                : selectedMemberships.length === 0
                ? "No membership types are selected, so auto-queue is paused."
                : `Auto-queue is limited to ${selectedMemberships.length} membership type${selectedMemberships.length !== 1 ? "s" : ""}.`}
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2">
              {membershipChoices.map((m) => (
                <Checkbox
                  key={m.value}
                  label={m.label}
                  checked={isMembershipFilterAll || selectedMemberships.includes(m.value)}
                  onChange={() => toggleMembershipEligibility(m.value)}
                  disabled={savingSettings}
                />
              ))}
            </div>
          </div>
        )}
      </Card>

      <Card padding="md">
        <CardHeader
          title="First-Time Guests"
          actions={
            <>
              {settings?.setupMode !== "manual" && (
                <Button
                  variant="secondary"
                  size="sm"
                  leftIcon={<MdSync aria-hidden="true" />}
                  loading={syncing}
                  onClick={handleSync}
                  title="Sync new guests from Planning Center"
                >
                  {syncing ? "Syncing…" : "Sync PCO"}
                </Button>
              )}
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<MdAdd aria-hidden="true" />}
                onClick={() => setShowAddGuest(true)}
              >
                Add Guest
              </Button>
              <Button
                size="sm"
                leftIcon={<MdUploadFile aria-hidden="true" />}
                onClick={() => setShowImport(true)}
              >
                Import CSV
              </Button>
            </>
          }
        />

        <div className="mb-5 mt-5 flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select
            aria-label="Survey status"
            className="w-full sm:w-44"
            value={statusFilter}
            onChange={(e) => handleStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            {Object.entries(SURVEY_STATUSES).map(([value, { label }]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Select>
          {membershipTypes.length > 0 && (
            <Select
              aria-label="Membership"
              className="w-full sm:w-48"
              value={membershipFilter}
              onChange={(e) => handleMembershipFilter(e.target.value)}
            >
              <option value="">All Memberships</option>
              {membershipChoices.map((m) => (
                <option key={m.value} value={m.value}>
                  {m.label}
                </option>
              ))}
            </Select>
          )}
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            delay={500}
            placeholder="Search by name or email…"
            ariaLabel="Search guests"
            className="w-full sm:ml-auto sm:w-64"
          />
        </div>

        {renderGuestTable()}
      </Card>

      <AddGuestModal
        open={showAddGuest}
        onClose={() => setShowAddGuest(false)}
        onCreated={() => {
          fetchGuests(1, search, statusFilter, sortBy, sortOrder);
          refreshDashboard();
        }}
      />
      <CsvImportModal
        open={showImport}
        onClose={() => setShowImport(false)}
        onImported={() => {
          fetchGuests(1, search, statusFilter, sortBy, sortOrder);
          refreshDashboard();
        }}
      />
    </PageContainer>
  );
};

export default PathwaysDashboard;
